import { useMemo, useState, useCallback } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Loader2,
  X,
  Check,
  Copy,
  ChevronDown,
  Expand,
  AlertTriangle,
  ArrowUpRightFromSquare,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import {
  FetchJourneysInfiniteQuery,
  computeGranularity,
  toMetricGranularity,
  type DateRangeInput,
  type IngestionFilter,
  type IngestionJourney,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
  IngestionBreakdown,
} from '@/lib/api/reporting/metrics';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { routes } from '@/lib/routing/route-paths';
import { IngestionEventTrace } from './IngestionEventTrace';
import {
  OUTCOME_HEX,
  OUTCOME_ORDER,
  getGroupMeta,
  formatDuration,
} from '../constants/stage-categories';
import { useIngestionManifest, type ResolvedGroup } from '../hooks/useIngestionManifest';
import { OutcomeIcon } from './OutcomeIcon';

interface IngestionEventJourneysProps {
  tenantId: string;
  dateRange: DateRangeInput;
  logWindow: DateRangeInput;
  filters: IngestionFilter;
  onJumpToHistogramBucket: (bucketTimestamp: string) => void;
  onResetLogWindow: () => void;
  onExtendRange: () => void;
  canGoNewer: boolean;
}

// --- Helpers ---

function formatWindowTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// --- Histogram chart data ---

interface HistogramDatum {
  bucket: string;
  bucketISO: string;
  [outcome: string]: string | number;
}

// --- Main component ---

export function IngestionEventJourneys({
  tenantId,
  dateRange,
  logWindow,
  filters,
  onJumpToHistogramBucket,
  onResetLogWindow,
  onExtendRange,
  canGoNewer,
}: IngestionEventJourneysProps) {
  const [selectedTrace, setSelectedTrace] = useState<{ correlationId: string; timestampHint: string } | null>(null);
  const { groups, getGroupForStage, stageDisplayNames } = useIngestionManifest();

  const searchInput = useMemo(
    () => ({
      ...logWindow,
      finalOutcome: filters.outcome || undefined,
      projectId: filters.projectId,
      videoProcessorConfigId: filters.videoProcessorConfigId,
      companyId: undefined, // Could wire up customer filter in future
    }),
    [logWindow, filters]
  );

  const {
    data,
    isLoading,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(FetchJourneysInfiniteQuery(tenantId, searchInput));

  const journeys = useMemo(
    () => data?.pages.flatMap((p) => p.journeys) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // --- Histogram (CubeJS metrics) ---
  const granularity = computeGranularity(dateRange);

  const { data: histogramData } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: IngestionBreakdown.Outcome,
      granularity: toMetricGranularity(granularity),
      dateStart: new Date(dateRange.dateStart),
      dateEnd: new Date(dateRange.dateEnd),
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const { chartData, outcomes } = useMemo(() => {
    if (!histogramData?.data?.length) return { chartData: [] as HistogramDatum[], outcomes: [] as string[] };

    const tickFormat =
      granularity === 'HOUR'
        ? {
            month: 'short' as const,
            day: 'numeric' as const,
            hour: '2-digit' as const,
            minute: '2-digit' as const,
          }
        : { month: 'short' as const, day: 'numeric' as const };

    const outcomeSet = new Set<string>();
    const byBucket: Record<string, Record<string, number>> = {};

    for (const point of histogramData.data) {
      const outcome = point.attributes?.['outcome'] ?? '';
      const bucket = point.key ?? '';
      outcomeSet.add(outcome);
      if (!byBucket[bucket]) byBucket[bucket] = {};
      byBucket[bucket][outcome] = (byBucket[bucket][outcome] ?? 0) + (point.value ?? 0);
    }

    const outcomes = OUTCOME_ORDER.filter((o) => outcomeSet.has(o));

    const sorted: HistogramDatum[] = Object.entries(byBucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, counts]) => ({
        bucket: new Date(bucket).toLocaleString(undefined, tickFormat),
        bucketISO: bucket,
        ...counts,
      }));

    return { chartData: sorted, outcomes };
  }, [histogramData, granularity]);

  // Log window overlay on histogram
  const { windowX1, windowX2 } = useMemo(() => {
    if (chartData.length === 0) return { windowX1: undefined, windowX2: undefined };

    const windowStart = new Date(logWindow.dateStart).getTime();
    const windowEnd = new Date(logWindow.dateEnd).getTime();

    let x1: string | undefined;
    let x2: string | undefined;

    for (const d of chartData) {
      const bucketTime = new Date(d.bucketISO).getTime();
      const bucketDuration =
        granularity === 'HOUR' ? 3600_000 : granularity === 'DAY' ? 86400_000 : 604800_000;
      const bucketEnd = bucketTime + bucketDuration;
      if (bucketTime < windowEnd && bucketEnd > windowStart) {
        if (!x1) x1 = d.bucket;
        x2 = d.bucket;
      }
    }

    return { windowX1: x1, windowX2: x2 };
  }, [logWindow, chartData, granularity]);

  const handleHistogramClick = useCallback(
    (state: { activePayload?: Array<{ payload?: HistogramDatum }> }) => {
      if (!state?.activePayload?.length) return;
      const payload = state.activePayload[0]?.payload as HistogramDatum | undefined;
      if (payload?.bucketISO) {
        onJumpToHistogramBucket(payload.bucketISO);
      }
    },
    [onJumpToHistogramBucket]
  );

  return (
    <div className="flex gap-3">
      <div className={cn('min-w-0 flex-1 space-y-2', selectedTrace && 'max-w-[60%]')}>
        {/* Histogram */}
        {chartData.length > 0 && (
          <div className="rounded-lg border border-[#2E3835] bg-[#1a1a1a] px-3 py-2">
            <div className="h-[120px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
                  barCategoryGap="8%"
                  onClick={handleHistogramClick}
                  style={{ cursor: 'pointer' }}
                >
                  <XAxis
                    dataKey="bucket"
                    tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }}
                    interval="preserveStartEnd"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis hide />
                  <Tooltip
                    content={({ active, label, payload }) => {
                      if (!active || !payload?.length) return null;
                      const total = payload.reduce((s, p) => s + ((p.value as number) ?? 0), 0);
                      return (
                        <div className="rounded-lg border border-[#2E3835] bg-black/95 px-3 py-2 text-xs backdrop-blur-sm">
                          <div className="mb-1 font-medium text-white/70">{label}</div>
                          {payload.map((p) => {
                            const val = (p.value as number) ?? 0;
                            if (val === 0) return null;
                            return (
                              <div
                                key={p.dataKey as string}
                                className="flex items-center gap-1.5 py-0.5"
                              >
                                <span
                                  className="inline-block h-2 w-2 shrink-0 rounded-sm"
                                  style={{ backgroundColor: p.color }}
                                />
                                <span className="text-white/70">{p.dataKey as string}</span>
                                <span className="ml-auto pl-3 tabular-nums text-white/50">
                                  {val.toLocaleString()}
                                </span>
                              </div>
                            );
                          })}
                          <div className="mt-1 border-t border-white/10 pt-1 text-white/40">
                            Total: {total.toLocaleString()} events
                          </div>
                        </div>
                      );
                    }}
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                  />
                  {windowX1 && windowX2 && (
                    <ReferenceArea
                      x1={windowX1}
                      x2={windowX2}
                      fill="rgba(209, 245, 81, 0.06)"
                      stroke="rgba(209, 245, 81, 0.25)"
                      strokeDasharray="3 3"
                    />
                  )}
                  {outcomes.map((outcome) => (
                    <Bar
                      key={outcome}
                      dataKey={outcome}
                      stackId="outcome"
                      fill={OUTCOME_HEX[outcome] ?? '#6b7280'}
                      radius={0}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-3 px-1 pt-1 text-[10px] text-white/40">
              {outcomes.map((outcome) => (
                <span key={outcome} className="flex items-center gap-1">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-sm"
                    style={{ backgroundColor: OUTCOME_HEX[outcome] }}
                  />
                  {outcome}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-400">
            Failed to load events: {error.message}
          </div>
        )}

        {/* Journey table */}
        <div className="overflow-hidden rounded-lg border border-[#2E3835] bg-[#1a1a1a]">
          {/* Load newer / reset button */}
          {canGoNewer && (
            <button
              onClick={onResetLogWindow}
              className="flex w-full items-center justify-center gap-1.5 border-b border-[#2E3835] bg-white/[0.02] py-1.5 text-[10px] text-white/40 transition-colors hover:bg-white/[0.04] hover:text-white/60"
            >
              Load newer events
            </button>
          )}

          {/* Table header */}
          <div className="flex items-center gap-0 border-b border-[#2E3835] bg-white/[0.02] text-[10px] font-medium uppercase tracking-wider text-white/30">
            <div className="w-[32px] shrink-0 px-1.5 py-1.5" />
            <div className="w-[130px] shrink-0 px-1 py-1.5">Time</div>
            <div className="w-[55px] shrink-0 px-1 py-1.5">Duration</div>
            <div className="w-[180px] shrink-0 px-1 py-1.5">Pipeline</div>
            <div className="w-[250px] shrink-0 px-1 py-1.5">Camera</div>
            <div className="w-[250px] shrink-0 px-1 py-1.5">Site</div>
            <div className="w-[250px] shrink-0 px-1 py-1.5">Company</div>
            <div className="min-w-0 flex-1 px-1 py-1.5">Reason</div>
          </div>

          {/* Journey rows */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-4 w-4 animate-spin text-white/30" />
            </div>
          ) : journeys.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-xs text-white/30">
              No events found in this time range
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03]">
              {journeys.map((journey) => (
                <JourneyRow
                  key={journey.correlationId}
                  journey={journey}
                  onTraceClick={(id, ts) => setSelectedTrace({ correlationId: id, timestampHint: ts })}
                  isSelected={journey.correlationId === selectedTrace?.correlationId}
                  groups={groups}
                  getGroupForStage={getGroupForStage}
                  stageDisplayNames={stageDisplayNames}
                />
              ))}
            </div>
          )}

          {/* Load older / extend range buttons */}
          {hasNextPage ? (
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="flex w-full items-center justify-center gap-1.5 border-t border-[#2E3835] bg-white/[0.02] py-1.5 text-[10px] text-white/40 transition-colors hover:bg-white/[0.04] hover:text-white/60 disabled:opacity-50"
            >
              {isFetchingNextPage ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              {isFetchingNextPage ? 'Loading...' : 'Load older events'}
            </button>
          ) : journeys.length > 0 ? (
            <button
              onClick={onExtendRange}
              className="flex w-full items-center justify-center gap-1.5 border-t border-dashed border-[#2E3835] bg-white/[0.01] py-1.5 text-[10px] text-white/30 transition-colors hover:bg-white/[0.03] hover:text-white/50"
            >
              <Expand className="h-3 w-3" />
              Extend time range to load more
            </button>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[#2E3835] bg-white/[0.02] px-3 py-1.5">
          <div className="flex items-center gap-2 text-[10px] text-white/40">
            <span className="font-mono">
              {formatWindowTime(logWindow.dateStart)} — {formatWindowTime(logWindow.dateEnd)}
            </span>
            {totalCount > 0 && (
              <span className="text-white/25">
                {journeys.length < totalCount
                  ? `(${journeys.length.toLocaleString()} of ${totalCount.toLocaleString()} events loaded)`
                  : `(${totalCount.toLocaleString()} events)`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Trace side panel */}
      {selectedTrace && (
        <div className="sticky top-16 max-h-[85vh] w-[40%] min-w-[380px] overflow-auto rounded-lg border border-[#2E3835] bg-[#1a1a1a] p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-white">Event Trace</h3>
            <button
              onClick={() => setSelectedTrace(null)}
              className="text-white/40 hover:text-white/80"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <IngestionEventTrace tenantId={tenantId} correlationId={selectedTrace.correlationId} timestampHint={selectedTrace.timestampHint} />
        </div>
      )}
    </div>
  );
}

// --- Pipeline path visualization ---

function PipelinePath({ journey, groups, getGroupForStage, stageDisplayNames }: {
  journey: IngestionJourney;
  groups: ResolvedGroup[];
  getGroupForStage: (stageKey: string) => string;
  stageDisplayNames: Map<string, string>;
}) {
  // Map each stage to its group and track worst outcome per group
  const groupOutcomes = useMemo(() => {
    const map = new Map<string, string>();

    for (const stage of journey.stages) {
      const groupKey = getGroupForStage(stage.stage);
      if (groupKey === 'unknown') continue;

      const current = map.get(groupKey);
      if (!current) {
        map.set(groupKey, stage.outcome);
      } else {
        const priority: Record<string, number> = { Failed: 3, Filtered: 2, Skipped: 1, Processed: 0 };
        if ((priority[stage.outcome] ?? 0) > (priority[current] ?? 0)) {
          map.set(groupKey, stage.outcome);
        }
      }
    }

    return map;
  }, [journey.stages, getGroupForStage]);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-0">
          {groups.map((group, i) => {
            const outcome = groupOutcomes.get(group.key);
            const isReached = !!outcome;

            const dotClass = !isReached
              ? 'border border-white/20 bg-transparent'
              : outcome === 'Failed'
                ? 'bg-red-500'
                : outcome === 'Filtered'
                  ? 'bg-amber-500'
                  : outcome === 'Processed'
                    ? 'bg-emerald-500'
                    : 'bg-gray-400';

            return (
              <div key={group.key} className="flex items-center">
                {i > 0 && (
                  <div className={cn(
                    'h-px w-2',
                    isReached ? 'bg-white/15' : 'bg-white/[0.06]'
                  )} />
                )}
                <span
                  className={cn(
                    'inline-block h-2 w-2 rounded-full shrink-0',
                    dotClass,
                  )}
                />
              </div>
            );
          })}
          <span className="ml-1.5 text-[8px] text-white/20 shrink-0">
            {journey.stageCount} stages
          </span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        sideOffset={8}
        className="w-auto min-w-[200px] max-w-[320px] border-[#2E3835] bg-[#1a1a1a] p-3 text-xs"
      >
        <div className="space-y-2">
          {groups.map((group, i) => {
            const outcome = groupOutcomes.get(group.key);
            const meta = getGroupMeta(group.key);
            const isReached = !!outcome;
            const stagesInGroup = journey.stages
              .filter((s) => getGroupForStage(s.stage) === group.key);

            const dotColor = !isReached
              ? 'border border-white/20 bg-transparent'
              : outcome === 'Failed'
                ? 'bg-red-500'
                : outcome === 'Filtered'
                  ? 'bg-amber-500'
                  : outcome === 'Processed'
                    ? 'bg-emerald-500'
                    : 'bg-gray-400';

            return (
              <div key={group.key} className="flex items-start gap-2">
                <div className="flex flex-col items-center pt-0.5">
                  <span className={cn('inline-block h-2.5 w-2.5 rounded-full shrink-0', dotColor)} />
                  {i < groups.length - 1 && (
                    <div className="h-full w-px min-h-[8px] bg-white/10 mt-0.5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={cn(
                    'font-medium flex items-center gap-1.5',
                    isReached ? 'text-white/80' : 'text-white/30'
                  )}>
                    <meta.icon className={cn(
                      'h-3 w-3 shrink-0',
                      isReached && (
                        outcome === 'Failed' ? 'text-red-400'
                          : outcome === 'Filtered' ? 'text-amber-400'
                          : outcome === 'Processed' ? 'text-emerald-400'
                          : 'text-gray-400'
                      )
                    )} />
                    {group.displayName}
                    {!isReached && (
                      <span className="text-[10px] font-normal text-white/20">skipped</span>
                    )}
                  </div>
                  {stagesInGroup.length > 0 && (
                    <div className="mt-0.5 space-y-0">
                      {stagesInGroup.map((s) => (
                        <div key={s.stage}>
                          <div className="flex items-center gap-1.5 text-white/50">
                            <span className={cn(
                              'inline-block h-1 w-1 rounded-full shrink-0',
                              s.outcome === 'Failed' ? 'bg-red-400'
                                : s.outcome === 'Filtered' ? 'bg-amber-400'
                                : s.outcome === 'Processed' ? 'bg-emerald-400'
                                : 'bg-gray-400'
                            )} />
                            {stageDisplayNames.get(s.stage) ?? s.stage}
                            {s.type && (
                              <span className="text-white/30">· {s.type}</span>
                            )}
                          </div>
                          {s.stopReason && (s.outcome === 'Filtered' || s.outcome === 'Failed') && (
                            <div className={cn(
                              'ml-3 text-[10px] truncate max-w-[240px]',
                              s.outcome === 'Failed' ? 'text-red-400/60' : 'text-amber-400/60'
                            )} title={s.stopReason}>
                              {s.stopReason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// --- CopyableId ---

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      title={id}
      className="group/copy inline-flex items-center gap-1 font-mono text-white/40 transition-colors hover:text-white/70"
    >
      <span>{id.slice(0, 8)}</span>
      {copied ? (
        <Check className="h-2.5 w-2.5 text-green-400" />
      ) : (
        <Copy className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover/copy:opacity-100" />
      )}
    </button>
  );
}

// --- JourneyRow ---

function JourneyRow({
  journey,
  onTraceClick,
  isSelected,
  groups,
  getGroupForStage,
  stageDisplayNames,
}: {
  journey: IngestionJourney;
  onTraceClick: (id: string, timestampHint: string) => void;
  isSelected: boolean;
  groups: ResolvedGroup[];
  getGroupForStage: (stageKey: string) => string;
  stageDisplayNames: Map<string, string>;
}) {
  return (
    <div
      onClick={() => onTraceClick(journey.correlationId, journey.firstEventAt)}
      className={cn(
        'flex items-center gap-0 border-l-2 px-0 py-1 text-[11px] transition-colors hover:bg-white/[0.02] cursor-pointer',
        isSelected ? 'border-l-blue-400/60 bg-blue-500/[0.08]' : 'border-l-transparent'
      )}
    >
      {/* Outcome icon */}
      <span className="w-[32px] shrink-0 flex items-center justify-center px-1">
        <OutcomeIcon outcome={journey.finalOutcome} />
      </span>

      {/* Time */}
      <span className="w-[130px] shrink-0 px-1 font-mono text-white/60">
        {new Date(journey.firstEventAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>

      {/* Duration */}
      <span className="w-[55px] shrink-0 px-1 font-mono text-white/40 tabular-nums">
        {formatDuration(journey.durationMs)}
      </span>

      {/* Pipeline path */}
      <span className="w-[180px] shrink-0 px-1">
        <PipelinePath
          journey={journey}
          groups={groups}
          getGroupForStage={getGroupForStage}
          stageDisplayNames={stageDisplayNames}
        />
      </span>

      {/* Camera */}
      <span className="w-[250px] shrink-0 truncate px-1">
        {journey.deviceName ? (
          <span
            className="block truncate text-white/50"
            title={[journey.deviceName, journey.deviceBrand, journey.videoProcessorType].filter(Boolean).join(' · ')}
          >
            {journey.deviceName}
          </span>
        ) : journey.videoProcessorConfigId ? (
          <CopyableId id={journey.videoProcessorConfigId} />
        ) : (
          <span className="text-white/15">—</span>
        )}
      </span>

      {/* Site */}
      <span className="w-[250px] shrink-0 truncate px-1">
        {journey.projectName && journey.projectId && journey.companyId ? (
          <Link
            to={routes.customerSite({ customerId: journey.companyId, siteId: journey.projectId })}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 truncate text-blue-400/70 hover:text-blue-400 hover:underline"
            title={`${journey.projectName} (${journey.projectId})`}
          >
            {journey.projectName}
            <ArrowUpRightFromSquare className="h-2.5 w-2.5 shrink-0" />
          </Link>
        ) : journey.projectName ? (
          <span className="truncate text-white/50" title={journey.projectName}>
            {journey.projectName}
          </span>
        ) : journey.projectId ? (
          <CopyableId id={journey.projectId} />
        ) : (
          <span className="text-white/15">—</span>
        )}
      </span>

      {/* Company */}
      <span className="w-[250px] shrink-0 truncate px-1">
        {journey.companyName ? (
          <span className="truncate text-white/50" title={journey.companyName}>
            {journey.companyName}
          </span>
        ) : journey.companyId ? (
          <CopyableId id={journey.companyId} />
        ) : (
          <span className="text-white/15">—</span>
        )}
      </span>

      {/* Reason */}
      <span className="min-w-0 flex-1 flex items-center gap-1.5 px-1 overflow-hidden">
        {journey.hasError && (
          <span
            title={journey.firstError ?? 'Error'}
            className="inline-flex shrink-0 items-center gap-0.5 rounded bg-red-500/10 px-1 py-0.5 text-[9px] text-red-400/70"
          >
            <AlertTriangle className="h-2.5 w-2.5" />
            err
          </span>
        )}
        {journey.wasFiltered && !journey.hasError && (
          <span className="inline-block shrink-0 rounded bg-amber-500/10 px-1 py-0.5 text-[9px] text-amber-400/60">
            filtered
          </span>
        )}
        {journey.monitoringEventId && !journey.hasError && !journey.wasFiltered && (
          <span className="inline-block shrink-0 rounded bg-emerald-500/10 px-1 py-0.5 text-[9px] text-emerald-400/60">
            alert
          </span>
        )}
        {(journey.firstStopReason || journey.firstReason || journey.firstError) && (
          <span className="truncate text-white/30" title={journey.firstStopReason || journey.firstReason || journey.firstError}>
            {journey.firstStopReason || journey.firstReason || journey.firstError}
          </span>
        )}
      </span>
    </div>
  );
}
